import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import { DateOnly } from "components/common/Date";
import { formatQuantity } from "utils/quantity";
import {
  SOURCE_TYPE_URL_MAP,
  type AnySourceType,
} from "constants/source-type.constants";
import { ToolInOutSourceType } from "features/ToolInOuts/constants/tool-in-out.constants";
import type { ToolInOut } from "features/ToolInOuts/types/tool-in-out.types";
import { getToolsOut } from "features/ToolInOuts/services/ToolInOutService";

function concernedPersonName(row: ToolInOut): string {
  const { firstName, lastName } = row.user ?? {};
  return [firstName, lastName].filter(Boolean).join(" ").trim() || "—";
}

function WorkCell({ row }: { row: ToolInOut }) {
  const isOther = row.sourceType === ToolInOutSourceType.OTHER;
  const label =
    (isOther ? row.otherDescription : row.workLabel)?.trim() ||
    row.workLabel ||
    "—";

  if (!isOther && row.sourceId != null) {
    const base = SOURCE_TYPE_URL_MAP[String(row.sourceType) as AnySourceType];
    if (base) {
      return (
        <Link
          to={`${base}/${row.sourceId}`}
          className="text-decoration-none link"
        >
          {label}
        </Link>
      );
    }
  }

  return <span>{label}</span>;
}

export default function ToolsListDashboardCard() {
  const columns: ColumnDef<ToolInOut>[] = useMemo(
    () => [
      {
        header: "Reference No",
        id: "referenceNo",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="small text-muted">
            {row.original.referenceNo?.trim() || "—"}
          </span>
        ),
      },
      {
        header: "Tool",
        id: "tool",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="fw-medium">{row.original.tool?.name ?? "—"}</span>
        ),
      },
      {
        header: "Concerned Person",
        id: "employee",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="small text-muted">
            {concernedPersonName(row.original)}
          </span>
        ),
      },
      {
        header: "Work",
        id: "work",
        enableColumnFilter: false,
        cell: ({ row }) => <WorkCell row={row.original} />,
      },
      {
        header: "Date Out",
        id: "dateOut",
        enableColumnFilter: false,
        cell: ({ row }) => (
          <span className="small text-muted">
            <DateOnly date={row.original.dateOut} />
          </span>
        ),
      },
      {
        header: "Quantity",
        id: "quantity",
        enableColumnFilter: false,
        cell: ({ row }) => formatQuantity(row.original.quantity),
      },
    ],
    [],
  );

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">Tools Out</h5>
        <Link
          to="/inventory-management/tool-in-outs"
          className="btn btn-sm btn-soft-primary"
        >
          View all
        </Link>
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getToolsOut}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
